import type {
  AskSageAnswer,
  AskSageQuestionInput,
  AskSageSession,
  AskSageSessionList,
  AskSageStats,
  ListAskSageSessionsQuery,
  AdminAssignMembershipInput,
  AdminUpdateMembershipInput,
  ApplyBaselineGuideInput,
  ApplyBaselineGuideResult,
  ApplicabilityItemType,
  ApplicabilityWorkspace,
  AuditEvent,
  AssignReviewInput,
  Channel,
  CreateChannelInput,
  CreateForbiddenExpressionInput,
  CreateMaterialCategoryInput,
  ForbiddenExpression,
  ImportForbiddenExpressionsInput,
  ImportForbiddenExpressionsResult,
  ListPlatformUsersQuery,
  ListReviewsQuery,
  CreateOrganizationInput,
  CreatePolicyInput,
  CreatePolicyVersionInput,
  CreateLessonInput,
  CreateReviewInput,
  CreateRuleInput,
  CreateRuleVersionInput,
  DraftLessonInput,
  DraftLessonResponse,
  InviteMemberInput,
  LessonCase,
  MaterialCategory,
  Member,
  Organization,
  OutputLanguage,
  OrganizationConfig,
  PlatformRole,
  PlatformUser,
  PlatformUserListPage,
  RuleOrigin,
  RuleSort,
  SetOrganizationVaultsageCredentialInput,
  Policy,
  PolicyListItem,
  PolicyVersionDiff,
  QueueHealth,
  RecordDecisionInput,
  ReviewDetail,
  ReviewListPage,
  ReviewMetrics,
  ReviewProgressEstimate,
  ReviewProgressItem,
  Rule,
  RuleListResponse,
  RuleReferenceLabel,
  SessionUser,
  CreateSupportMessageInput,
  CreateSupportTicketInput,
  ListSupportTicketsQuery,
  SupportInboxSummary,
  SupportInspection,
  SupportReportContext,
  SupportReportContextQuery,
  SupportTicketDetail,
  SupportTicketListPage,
  CloseSupportTicketInput,
  UpdateSupportTicketInput,
  TestableRule,
  TestLessonResponse,
  SelectOrganizationInput,
  UpdateChannelInput,
  UpdateForbiddenExpressionInput,
  UpdateMaterialCategoryInput,
  UpdateMemberInput,
  UpdateOrganizationConfigInput,
  UpdateOrganizationInput,
  UpdateLlmSettingsInput,
  LlmSettings,
  UpdatePolicyInput,
  UpdateProfileInput,
  UpdateApplicabilityInput,
  UpdateFindingReviewInput,
  AiQualityReport,
  AiQualityQuery,
  EngineVersionQualityReport,
  VaultsageCredentialStatus,
  VaultsageOrphanScan,
  VaultsageStorageLocation,
} from "@compliance-sage/contracts";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

async function parseResponse<T>(
  status: number,
  statusText: string,
  headers: { get(name: string): string | null },
  getJson: () => Promise<unknown>,
  getBlob: () => Promise<Blob>,
): Promise<T> {
  if (!(status >= 200 && status < 300)) {
    let message = statusText;
    let code: string | undefined;
    try {
      const data = (await getJson()) as { error?: string; code?: string };
      message = data.error ?? message;
      code = data.code;
    } catch {
      /* ignore */
    }
    throw new ApiError(status, message, code);
  }
  if (status === 204) return undefined as T;
  const contentType = headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return (await getJson()) as T;
  return (await getBlob()) as unknown as T;
}

/** Serialises a query object, dropping unset values, into a `?a=b` suffix. */
function queryString(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  return params.size ? `?${params}` : "";
}

/**
 * Reads the Ask Sage event stream. Buffers across chunk boundaries because a
 * chunk can split an event, or a multi-byte Korean character, in half.
 */
async function readAnswerStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (answerSoFar: string) => void,
): Promise<AskSageAnswer> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer: AskSageAnswer | null = null;

  const handle = (rawEvent: string) => {
    let name = "message";
    const dataLines: string[] = [];
    for (const line of rawEvent.split(/\r?\n/)) {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    if (dataLines.length === 0) return;
    let payload: unknown;
    try {
      payload = JSON.parse(dataLines.join("\n"));
    } catch {
      return;
    }
    if (name === "delta") {
      const text = (payload as { answer?: unknown }).answer;
      if (typeof text === "string") onDelta(text);
    } else if (name === "done") {
      answer = payload as AskSageAnswer;
    } else if (name === "error") {
      const message = (payload as { error?: string }).error;
      throw new ApiError(500, message ?? "Ask Sage failed");
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let separator = buffer.search(/\r?\n\r?\n/);
    while (separator !== -1) {
      const rawEvent = buffer.slice(0, separator);
      buffer = buffer.slice(separator + (buffer[separator] === "\r" ? 4 : 2));
      handle(rawEvent);
      separator = buffer.search(/\r?\n\r?\n/);
    }
  }
  if (buffer.trim().length > 0) handle(buffer);
  if (!answer) throw new ApiError(500, "Ask Sage stream ended without an answer");
  return answer;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...(init.headers ?? {}),
    },
  });
  return parseResponse<T>(res.status, res.statusText, res.headers, () => res.json(), () => res.blob());
}

// Bytes-sent upload progress requires XMLHttpRequest — fetch has no hook for
// it — so this is a separate request path used only where the caller wants
// to show upload progress for a large multipart body.
function requestWithUploadProgress<T>(
  path: string,
  form: FormData,
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}${path}`);
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      };
    }
    xhr.onload = () => {
      const headers = { get: (name: string) => xhr.getResponseHeader(name) };
      parseResponse<T>(
        xhr.status,
        xhr.statusText,
        headers,
        async () => JSON.parse(xhr.responseText || "null"),
        async () => new Blob([xhr.response]),
      ).then(resolve, reject);
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(form);
  });
}

export const api = {
  // Auth
  startLogin: () => request<{ authorizeUrl: string }>("/auth/login"),
  me: () => request<SessionUser>("/auth/me"),
  updateProfile: (input: UpdateProfileInput) =>
    request<SessionUser>("/auth/me", { method: "PATCH", body: JSON.stringify(input) }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),

  // Organizations
  listOrganizations: () => request<Organization[]>("/organizations"),
  createOrganization: (input: CreateOrganizationInput) =>
    request<Organization>("/organizations", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateOrganization: (id: string, input: UpdateOrganizationInput) =>
    request<Organization>(`/organizations/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  setOrganizationVaultsageCredential: (id: string, input: SetOrganizationVaultsageCredentialInput) =>
    request<VaultsageCredentialStatus>(`/organizations/${id}/vaultsage-credential`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  verifyOrganizationStorage: (id: string) =>
    request<VaultsageStorageLocation>(`/organizations/${id}/vaultsage-storage/verify`, {
      method: "POST",
    }),
  applyBaselineGuide: (id: string, input: ApplyBaselineGuideInput) =>
    request<ApplyBaselineGuideResult>(`/organizations/${id}/baseline-guide`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteOrganization: (id: string) =>
    request<void>(`/organizations/${id}`, { method: "DELETE" }),
  selectOrganization: (input: SelectOrganizationInput) =>
    request<{ ok: true }>("/organizations/select", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  listVaultsageOrphans: () => request<VaultsageOrphanScan>("/vaultsage-orphans"),
  cleanupVaultsageOrphan: (directoryId: string) =>
    request<void>(`/vaultsage-orphans/${encodeURIComponent(directoryId)}/cleanup`, {
      method: "POST",
    }),

  // Platform LLM settings (Super Admin)
  getLlmSettings: () => request<LlmSettings>("/admin/llm-settings"),
  updateLlmSettings: (input: UpdateLlmSettingsInput) =>
    request<LlmSettings>("/admin/llm-settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  // Organization configuration
  getConfig: () => request<OrganizationConfig>("/config"),
  updateConfig: (input: UpdateOrganizationConfigInput) =>
    request<OrganizationConfig>("/config", { method: "PATCH", body: JSON.stringify(input) }),
  uploadApprovalSeal: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<OrganizationConfig>("/config/approval-seal", { method: "PUT", body: form });
  },
  removeApprovalSeal: () =>
    request<OrganizationConfig>("/config/approval-seal", { method: "DELETE" }),
  approvalSealUrl: () => `${BASE}/config/approval-seal`,

  // Members
  listMembers: () => request<Member[]>("/members"),
  inviteMember: (input: InviteMemberInput) =>
    request<Member>("/members", { method: "POST", body: JSON.stringify(input) }),
  updateMember: (id: string, input: UpdateMemberInput) =>
    request<Member>(`/members/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteMember: (id: string) => request<void>(`/members/${id}`, { method: "DELETE" }),

  // Platform users (Super Admin: all accounts across every organization)
  listPlatformUsers: (query: ListPlatformUsersQuery = {}) =>
    request<PlatformUserListPage>(`/admin/users${queryString(query)}`),
  getPlatformUser: (userId: string) => request<PlatformUser>(`/admin/users/${userId}`),
  updatePlatformRole: (userId: string, platformRole: PlatformRole) =>
    request<PlatformUser>(`/admin/users/${userId}/platform-role`, {
      method: "PATCH",
      body: JSON.stringify({ platformRole }),
    }),
  adminAssignMembership: (input: AdminAssignMembershipInput) =>
    request<Member>("/admin/users/memberships", { method: "POST", body: JSON.stringify(input) }),
  adminUpdateMembership: (membershipId: string, input: AdminUpdateMembershipInput) =>
    request<Member>(`/admin/users/memberships/${membershipId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  adminDeleteMembership: (membershipId: string, organizationId: string) =>
    request<void>(
      `/admin/users/memberships/${membershipId}?organizationId=${encodeURIComponent(organizationId)}`,
      { method: "DELETE" },
    ),

  // Policies
  listPolicies: () => request<PolicyListItem[]>("/policies"),
  getPolicy: (id: string) => request<Policy>(`/policies/${id}`),
  createPolicy: (data: CreatePolicyInput, file: File | null) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    if (file) form.append("file", file);
    return request<Policy>("/policies", { method: "POST", body: form });
  },
  activatePolicyVersion: (policyId: string, versionId: string) =>
    request<Policy>(`/policies/${policyId}/versions/${versionId}/activate`, { method: "POST" }),
  reprocessPolicyVersion: (policyId: string, versionId: string) =>
    request<Policy>(`/policies/${policyId}/versions/${versionId}/reprocess`, { method: "POST" }),
  normalizePolicyVersion: (policyId: string, versionId: string) =>
    request<Policy>(`/policies/${policyId}/versions/${versionId}/normalize`, { method: "POST" }),
  retirePolicy: (policyId: string) =>
    request<{ ok: true }>(`/policies/${policyId}/retire`, { method: "POST" }),
  archivePolicy: (policyId: string) =>
    request<Policy>(`/policies/${policyId}/archive`, { method: "POST" }),
  unarchivePolicy: (policyId: string) =>
    request<Policy>(`/policies/${policyId}/unarchive`, { method: "POST" }),
  renamePolicy: (policyId: string, data: UpdatePolicyInput) =>
    request<Policy>(`/policies/${policyId}`, { method: "PATCH", body: JSON.stringify(data) }),
  deletePolicy: (policyId: string) => request<Policy>(`/policies/${policyId}`, { method: "DELETE" }),
  createPolicyVersion: (policyId: string, data: CreatePolicyVersionInput, file: File | null) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    if (file) form.append("file", file);
    return request<Policy>(`/policies/${policyId}/versions`, { method: "POST", body: form });
  },
  policyVersionDiff: (policyId: string, versionId: string, fromVersionId: string) =>
    request<PolicyVersionDiff>(
      `/policies/${policyId}/versions/${versionId}/diff?from=${encodeURIComponent(fromVersionId)}`,
    ),
  policyVersionPreviewUrl: (policyId: string, versionId: string) =>
    `${BASE}/policies/${policyId}/versions/${versionId}/preview`,
  policyVersionDownloadUrl: (policyId: string, versionId: string) =>
    `${BASE}/policies/${policyId}/versions/${versionId}/download`,

  // Unified policy/rule applicability
  applicabilityWorkspace: () => request<ApplicabilityWorkspace>("/applicability"),
  cloneApplicabilityDraft: (
    itemType: ApplicabilityItemType,
    itemId: string,
    versionId: string,
  ) =>
    request<{ versionId: string }>(
      `/applicability/${itemType}/${itemId}/versions/${versionId}/draft`,
      { method: "POST" },
    ),
  updateApplicability: (
    itemType: ApplicabilityItemType,
    itemId: string,
    versionId: string,
    data: UpdateApplicabilityInput,
  ) =>
    request<void>(`/applicability/${itemType}/${itemId}/versions/${versionId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  // Channels (매체)
  listChannels: (options: { activeOnly?: boolean } = {}) =>
    request<Channel[]>(`/channels${options.activeOnly === false ? "?activeOnly=false" : ""}`),
  createChannel: (data: CreateChannelInput) =>
    request<Channel>("/channels", { method: "POST", body: JSON.stringify(data) }),
  updateChannel: (id: string, data: UpdateChannelInput) =>
    request<Channel>(`/channels/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  // A channel referenced by a review cannot be removed, so the server may
  // deactivate it instead and report that it did.
  deleteChannel: (id: string) =>
    request<{ deleted: false; channel: Channel } | undefined>(`/channels/${id}`, {
      method: "DELETE",
    }),

  // Material categories (구분1/구분2)
  listMaterialCategories: (options: { activeOnly?: boolean } = {}) =>
    request<MaterialCategory[]>(
      `/material-categories${options.activeOnly === false ? "?activeOnly=false" : ""}`,
    ),
  createMaterialCategory: (data: CreateMaterialCategoryInput) =>
    request<MaterialCategory>("/material-categories", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  applyMaterialCategoryTemplate: () =>
    request<MaterialCategory[]>("/material-categories/template", { method: "POST" }),
  updateMaterialCategory: (id: string, data: UpdateMaterialCategoryInput) =>
    request<MaterialCategory>(`/material-categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteMaterialCategory: (id: string) =>
    request<{ deleted: false; category: MaterialCategory } | undefined>(
      `/material-categories/${id}`,
      { method: "DELETE" },
    ),

  // Forbidden expression dictionary (금지 표현 사전)
  listDictionary: (options: { activeOnly?: boolean } = {}) =>
    request<ForbiddenExpression[]>(
      `/dictionary${options.activeOnly ? "?activeOnly=true" : ""}`,
    ),
  createDictionaryEntry: (data: CreateForbiddenExpressionInput) =>
    request<ForbiddenExpression>("/dictionary", { method: "POST", body: JSON.stringify(data) }),
  updateDictionaryEntry: (id: string, data: UpdateForbiddenExpressionInput) =>
    request<ForbiddenExpression>(`/dictionary/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  deleteDictionaryEntry: (id: string) =>
    request<void>(`/dictionary/${id}`, { method: "DELETE" }),
  importDictionary: (data: ImportForbiddenExpressionsInput) =>
    request<ImportForbiddenExpressionsResult>("/dictionary/import", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Rules
  listRules: (
    page = 1,
    pageSize = 20,
    origin?: RuleOrigin,
    options: { search?: string; sort?: RuleSort } = {},
  ) =>
    request<RuleListResponse>(
      `/rules?page=${page}&pageSize=${pageSize}${origin ? `&origin=${origin}` : ""}` +
        `${options.search ? `&search=${encodeURIComponent(options.search)}` : ""}` +
        `${options.sort ? `&sort=${options.sort}` : ""}`,
    ),
  getRule: (ruleId: string) => request<Rule>(`/rules/${ruleId}`),
  createRule: (data: CreateRuleInput) =>
    request<Rule>("/rules", { method: "POST", body: JSON.stringify(data) }),
  createRuleVersion: (ruleId: string, data: CreateRuleVersionInput) =>
    request<Rule>(`/rules/${ruleId}/versions`, { method: "POST", body: JSON.stringify(data) }),
  countDraftLessons: () => request<{ count: number }>("/rules/lessons/drafts"),
  activateDraftLessons: () =>
    request<{ activated: number; skipped: Array<{ ruleId: string; title: string; reason: string }> }>(
      "/rules/lessons/activate-drafts",
      { method: "POST" },
    ),
  activateRuleVersion: (ruleId: string, versionId: string) =>
    request<Rule>(`/rules/${ruleId}/versions/${versionId}/activate`, { method: "POST" }),
  retireRule: (ruleId: string) =>
    request<{ ok: true }>(`/rules/${ruleId}/retire`, { method: "POST" }),
  deleteRule: (ruleId: string) =>
    request<void>(`/rules/${ruleId}`, { method: "DELETE" }),
  uploadRuleReference: (
    ruleId: string,
    versionId: string,
    input: { file: File; label: RuleReferenceLabel; note?: string },
  ) => {
    const form = new FormData();
    form.append("file", input.file);
    form.append("label", input.label);
    if (input.note) form.append("note", input.note);
    return request<Rule>(`/rules/${ruleId}/versions/${versionId}/references`, {
      method: "POST",
      body: form,
    });
  },
  deleteRuleReference: (ruleId: string, versionId: string, imageId: string) =>
    request<void>(`/rules/${ruleId}/versions/${versionId}/references/${imageId}`, {
      method: "DELETE",
    }),
  ruleReferencePreviewUrl: (ruleId: string, versionId: string, imageId: string) =>
    `${BASE}/rules/${ruleId}/versions/${versionId}/references/${imageId}/preview`,

  // Educate Sage: case-derived lessons
  draftLesson: (
    data: DraftLessonInput,
    files: { originalFile: File | null; feedbackFile: File | null; modifiedFile: File | null },
  ) => {
    const form = new FormData();
    if (data.originalText) form.append("originalText", data.originalText);
    if (data.modifiedText) form.append("modifiedText", data.modifiedText);
    if (data.feedbackComments) form.append("feedbackComments", data.feedbackComments);
    if (files.originalFile) form.append("originalFile", files.originalFile);
    if (files.feedbackFile) form.append("feedbackFile", files.feedbackFile);
    if (files.modifiedFile) form.append("modifiedFile", files.modifiedFile);
    return request<DraftLessonResponse>("/rules/cases/draft", { method: "POST", body: form });
  },
  createLesson: (data: CreateLessonInput) =>
    request<Rule>("/rules/cases", { method: "POST", body: JSON.stringify(data) }),
  getLessonCase: (ruleId: string) => request<LessonCase | null>(`/rules/${ruleId}/case`),
  updateLessonSummary: (ruleId: string, generatedSummary: string) =>
    request<LessonCase>(`/rules/${ruleId}/case`, {
      method: "PATCH",
      body: JSON.stringify({ generatedSummary }),
    }),
  listTestableRules: (excludeRuleId?: string) =>
    request<TestableRule[]>(
      `/rules/testable${excludeRuleId ? `?excludeRuleId=${encodeURIComponent(excludeRuleId)}` : ""}`,
    ),
  testLesson: (
    ruleId: string,
    input: { file: File | null; bodyText?: string; additionalRuleIds?: string[] },
  ) => {
    const form = new FormData();
    if (input.file) form.append("file", input.file);
    if (input.bodyText) form.append("bodyText", input.bodyText);
    if (input.additionalRuleIds?.length) {
      form.append("additionalRuleIds", JSON.stringify(input.additionalRuleIds));
    }
    return request<TestLessonResponse>(`/rules/${ruleId}/test`, { method: "POST", body: form });
  },

  // Reviews
  listReviews: (query: ListReviewsQuery = {}) =>
    request<ReviewListPage>(`/reviews${queryString(query)}`),
  reviewMetrics: () => request<ReviewMetrics>("/reviews/metrics"),
  knowledgeTestReviews: () => request<ReviewListPage>("/reviews/knowledge-tests"),
  reviewProgress: () => request<ReviewProgressItem[]>("/reviews/progress"),
  reviewProgressEstimate: () => request<ReviewProgressEstimate>("/reviews/progress/estimate"),
  reviewProgressFor: (id: string) => request<ReviewProgressItem | null>(`/reviews/${id}/progress`),
  getReview: (id: string) => request<ReviewDetail>(`/reviews/${id}`),
  deleteReview: (id: string) => request<void>(`/reviews/${id}`, { method: "DELETE" }),
  createReview: (data: CreateReviewInput, file: File | null, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    if (file) form.append("file", file);
    return requestWithUploadProgress<ReviewDetail>("/reviews", form, onProgress);
  },
  resubmitReview: (
    id: string,
    data: {
      channelId?: string;
      categoryId?: string | null;
      bodyText?: string;
      outputLanguage?: OutputLanguage;
    },
    file: File | null,
  ) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    if (file) form.append("file", file);
    return request<ReviewDetail>(`/reviews/${id}/resubmit`, { method: "POST", body: form });
  },
  rerunReview: (id: string) =>
    request<ReviewDetail>(`/reviews/${id}/rerun`, { method: "POST" }),
  recordDecision: (id: string, data: RecordDecisionInput) =>
    request<ReviewDetail>(`/reviews/${id}/decision`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  assignReview: (id: string, data: AssignReviewInput) =>
    request<ReviewDetail>(`/reviews/${id}/assignment`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  updateFindingReview: (reviewId: string, findingId: string, data: UpdateFindingReviewInput) =>
    request<ReviewDetail>(`/reviews/${reviewId}/findings/${findingId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  refreshLocationSuggestions: (reviewId: string) =>
    request<ReviewDetail>(`/reviews/${reviewId}/location-suggestions`, { method: "POST" }),
  reportUrl: (id: string) => `${BASE}/reviews/${id}/report`,
  approvedDocumentUrl: (id: string) => `${BASE}/reviews/${id}/approved-document`,
  materialPreviewUrl: (id: string, revisionId?: string) =>
    `${BASE}/reviews/${id}/material-preview${
      revisionId ? `?revision=${encodeURIComponent(revisionId)}` : ""
    }`,

  // AI quality: what the accumulated human decisions say about whether the
  // system may decide by itself, and where the coverage is going.
  aiQualityReport: (query: AiQualityQuery = {}) =>
    request<AiQualityReport>(`/ai-quality${queryString(query)}`),
  engineVersionQuality: (query: AiQualityQuery = {}) =>
    request<EngineVersionQualityReport>(`/ai-quality/engine-versions${queryString(query)}`),

  // Audit
  listAudit: () => request<AuditEvent[]>("/audit"),

  // Analysis queue health
  queueHealth: () => request<QueueHealth>("/queue-health"),

  // Support: a client reports a problem from the screen it happened on, and
  // the server attaches the diagnostic context itself.
  createSupportTicket: (input: CreateSupportTicketInput) =>
    request<SupportTicketDetail>("/support/tickets", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  listSupportTickets: (query: ListSupportTicketsQuery = {}) =>
    request<SupportTicketListPage>(`/support/tickets${queryString(query)}`),
  getSupportTicket: (id: string) => request<SupportTicketDetail>(`/support/tickets/${id}`),
  closeSupportTicket: (id: string, input: CloseSupportTicketInput) =>
    request<SupportTicketDetail>(`/support/tickets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  /** The context a report will carry, previewed before the reporter submits. */
  supportReportContext: (query: SupportReportContextQuery) =>
    request<SupportReportContext>(`/support/report-context${queryString(query)}`),
  replySupportTicket: (id: string, input: CreateSupportMessageInput) =>
    request<SupportTicketDetail>(`/support/tickets/${id}/messages`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // Support inbox (Super Admin: every organization's tickets in one queue)
  supportInboxSummary: (query: { organizationId?: string } = {}) =>
    request<SupportInboxSummary>(`/admin/support/summary${queryString(query)}`),
  listAllSupportTickets: (query: ListSupportTicketsQuery = {}) =>
    request<SupportTicketListPage>(`/admin/support/tickets${queryString(query)}`),
  getAdminSupportTicket: (id: string) =>
    request<SupportTicketDetail>(`/admin/support/tickets/${id}`),
  updateSupportTicket: (id: string, input: UpdateSupportTicketInput) =>
    request<SupportTicketDetail>(`/admin/support/tickets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  /** The material and AI result behind a ticket, read live and audited server-side. */
  inspectSupportTicket: (id: string) =>
    request<SupportInspection>(`/admin/support/tickets/${id}/inspection`),
  /** Streamed through the API, so the client's storage credentials stay server-side. */
  supportTicketMaterialFileUrl: (id: string) =>
    `${BASE}/admin/support/tickets/${id}/material-file`,
  replyAdminSupportTicket: (id: string, input: CreateSupportMessageInput) =>
    request<SupportTicketDetail>(`/admin/support/tickets/${id}/messages`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // Ask Sage: grounded questions asked before a material is submitted.
  askSage: (input: AskSageQuestionInput) =>
    request<AskSageAnswer>("/ask-sage/questions", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /**
   * The same call over SSE. `onDelta` receives the answer as written so far —
   * already assembled, so the caller just renders it — and the promise resolves
   * with the stored turn. Falls back to the buffered endpoint when the browser
   * or a proxy gives us no readable stream.
   */
  askSageStream: async (
    input: AskSageQuestionInput,
    onDelta: (answerSoFar: string) => void,
  ): Promise<AskSageAnswer> => {
    const res = await fetch(`${BASE}/ask-sage/questions/stream`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      throw new ApiError(res.status, res.statusText);
    }
    if (!res.body) {
      return request<AskSageAnswer>("/ask-sage/questions", {
        method: "POST",
        body: JSON.stringify(input),
      });
    }
    return readAnswerStream(res.body, onDelta);
  },
  listAskSageSessions: () => request<AskSageSessionList>("/ask-sage/sessions"),
  getAskSageSession: (id: string) => request<AskSageSession>(`/ask-sage/sessions/${id}`),

  // Ask Sage log (Admin: the organization's transcripts and usage)
  listAskSageLog: (query: Partial<ListAskSageSessionsQuery> = {}) =>
    request<AskSageSessionList>(`/ask-sage/log/sessions${queryString(query)}`),
  askSageLogStats: (query: Partial<ListAskSageSessionsQuery> = {}) =>
    request<AskSageStats>(`/ask-sage/log/stats${queryString(query)}`),
  getAskSageLogSession: (id: string) => request<AskSageSession>(`/ask-sage/log/sessions/${id}`),

  // Ask Sage log (Super Admin: every organization)
  listPlatformAskSageLog: (query: Partial<ListAskSageSessionsQuery> = {}) =>
    request<AskSageSessionList>(`/admin/ask-sage/sessions${queryString(query)}`),
  platformAskSageStats: (query: Partial<ListAskSageSessionsQuery> = {}) =>
    request<AskSageStats>(`/admin/ask-sage/stats${queryString(query)}`),
  getPlatformAskSageSession: (id: string) =>
    request<AskSageSession>(`/admin/ask-sage/sessions/${id}`),
};
